import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import adminService from '../../../services/adminService';

const EmployersManagement = () => {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    phone: '',
    location: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadEmployers();
  }, []);

  const loadEmployers = async () => {
    try {
      setLoading(true);
      const data = await adminService?.getAllEmployers();
      setEmployers(data || []);
    } catch (error) {
      console.error('Error loading employers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (employer = null) => {
    if (employer) {
      setEditingEmployer(employer);
      setFormData({
        email: employer?.email,
        password: '',
        full_name: employer?.full_name || '',
        company_name: employer?.company_name || '',
        phone: employer?.phone || '',
        location: employer?.location || ''
      });
    } else {
      setEditingEmployer(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        company_name: '',
        phone: '',
        location: ''
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEmployer(null);
    setFormData({ email: '', password: '', full_name: '', company_name: '', phone: '', location: '' });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is verplicht';
    }
    if (!editingEmployer && !formData?.password?.trim()) {
      newErrors.password = 'Wachtwoord is verplicht';
    }
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Naam is verplicht';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingEmployer) {
        const updates = {
          full_name: formData?.full_name,
          company_name: formData?.company_name,
          phone: formData?.phone,
          location: formData?.location
        };
        await adminService?.updateEmployer(editingEmployer?.id, updates);
      } else {
        await adminService?.createEmployer(formData);
      }
      await loadEmployers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving employer:', error);
      setErrors({ submit: error?.message });
    }
  };

  const handleDelete = async (employerId) => {
    if (!window.confirm('Weet je zeker dat je deze opdrachtgever wilt verwijderen?')) return;

    try {
      await adminService?.deleteEmployer(employerId);
      await loadEmployers();
    } catch (error) {
      console.error('Error deleting employer:', error);
      alert('Fout bij verwijderen: ' + error?.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Opdrachtgevers Beheer</h2>
          <p className="text-sm text-muted-foreground">Beheer opdrachtgevers accounts</p>
        </div>
        <Button onClick={() => handleOpenModal()} iconName="Plus">
          Opdrachtgever Toevoegen
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : (
        <div 
          className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
          style={{
            boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
          }}
        >
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Naam</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Bedrijf</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Locatie</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {employers?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                    Geen opdrachtgevers gevonden
                  </td>
                </tr>
              ) : (
                employers?.map((employer) => (
                  <tr key={employer?.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground font-medium">{employer?.full_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{employer?.company_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{employer?.email}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{employer?.location || '-'}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => handleOpenModal(employer)}
                      >
                        Bewerken
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(employer?.id)}
                      >
                        Verwijderen
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 z-1030 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleCloseModal} />
          <div 
            className="relative bg-white border-2 border-slate-200 rounded-lg w-full max-w-lg"
            style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 10px 30px rgba(0,0,0,0.12)'
            }}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {editingEmployer ? 'Opdrachtgever Bewerken' : 'Nieuwe Opdrachtgever'}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="email@voorbeeld.nl"
                value={formData?.email}
                onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                error={errors?.email}
                disabled={!!editingEmployer}
                required
              />

              {!editingEmployer && (
                <Input
                  label="Wachtwoord"
                  type="password"
                  placeholder="Minimaal 6 tekens"
                  value={formData?.password}
                  onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                  error={errors?.password}
                  required
                />
              )}

              <Input
                label="Volledige Naam"
                type="text"
                placeholder="Jan de Vries"
                value={formData?.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e?.target?.value })}
                error={errors?.full_name}
                required
              />

              <Input
                label="Bedrijfsnaam"
                type="text"
                placeholder="Optioneel"
                value={formData?.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e?.target?.value })}
              />

              <Input
                label="Telefoon"
                type="tel"
                placeholder="+31 6 12345678"
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
              />

              <Input
                label="Locatie"
                type="text"
                placeholder="Amsterdam"
                value={formData?.location}
                onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
              />

              {errors?.submit && (
                <div className="text-sm text-error">{errors?.submit}</div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Annuleren
                </Button>
                <Button type="submit">
                  {editingEmployer ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployersManagement;